/**
 * The floating browser preview's frame: drag, resize, and the hover pill.
 *
 * Ported from t3code (`apps/web/src/components/preview/ThreadPreviewMiniPlayer.tsx`,
 * `MiniPlayerShell`, MIT, pin `e3e7cc3f`). Adaptations: forge's own mini
 * player store; the composer obstacle is measured from forge's composer dock
 * attributes (`data-chat-composer-stack` / `data-chat-composer-dock`) found
 * from the positioned ancestor; pointer capture is feature-checked so jsdom
 * suites can exercise the same gesture handlers; tappable surfaces project
 * the 48px hit pad for every pointer (DESIGN.md -> "Mobile-First
 * Responsiveness").
 *
 * @module BrowserMiniPlayerShell
 */
import type { ScopedThreadRef } from "@cmmd-forge/contracts";
import type { ReactNode } from "react";
import { useLayoutEffect, useRef, useState } from "react";

import type { BrowserViewportResizeDirection } from "~/browser/browserViewportLayout";
import { HIT_AREA_PAD_CLASS } from "~/lib/touchTarget";
import { cn } from "~/lib/utils";

import {
  BROWSER_MINI_PLAYER_BACKDROP_Z_INDEX,
  BROWSER_MINI_PLAYER_CHROME_Z_INDEX,
  BROWSER_MINI_PLAYER_CONTENT_Z_INDEX,
  BROWSER_MINI_PLAYER_CORNER_RADIUS,
  type BrowserMiniPlayerFrame,
  NO_BROWSER_MINI_PLAYER_OBSTACLES,
  resolveBrowserMiniPlayerFrame,
} from "./browserMiniPlayerLayout";
import { BrowserMiniPlayerPill } from "./BrowserMiniPlayerPill";
import {
  browserMiniPlayerSourceKey,
  useBrowserMiniPlayerStore,
  type BrowserMiniPlayerSize,
  type BrowserMiniPlayerState,
} from "./browserMiniPlayerStore";
import {
  measureBrowserMiniPlayerLayout,
  sameBrowserMiniPlayerLayout,
  useBrowserMiniPlayerGestures,
  type BrowserMiniPlayerShellLayout,
} from "./useBrowserMiniPlayerGestures";

// Invisible grab zones straddling each edge; the cursor is the only affordance.
// The visual zone keeps the 8-16px rail the frame implies; the hit pad lifts
// the tappable area to the 48px touch minimum for every pointer.
const RESIZE_HANDLES: ReadonlyArray<{
  readonly direction: BrowserViewportResizeDirection;
  readonly className: string;
}> = [
  { direction: "north", className: "inset-x-0 -top-1 h-2 cursor-ns-resize" },
  { direction: "south", className: "inset-x-0 -bottom-1 h-2 cursor-ns-resize" },
  { direction: "west", className: "inset-y-0 -left-1 w-2 cursor-ew-resize" },
  { direction: "east", className: "inset-y-0 -right-1 w-2 cursor-ew-resize" },
  { direction: "northwest", className: "-left-2 -top-2 size-4 cursor-nwse-resize" },
  { direction: "northeast", className: "-right-2 -top-2 size-4 cursor-nesw-resize" },
  { direction: "southwest", className: "-bottom-2 -left-2 size-4 cursor-nesw-resize" },
  { direction: "southeast", className: "-bottom-2 -right-2 size-4 cursor-nwse-resize" },
];

export interface BrowserMiniPlayerShellProps {
  readonly threadRef: ScopedThreadRef;
  readonly miniPlayer: BrowserMiniPlayerState;
  readonly sourceSize: BrowserMiniPlayerSize;
  readonly label: string;
  readonly recording?: boolean;
  readonly onOpenInPanel: () => void;
  readonly children: (frame: BrowserMiniPlayerFrame) => ReactNode;
}

/**
 * The frame, drag/resize gestures, and hover pill of the floating player.
 * The frame radius and the pill inset are one decision so the separately
 * composited edges stay aligned.
 */
export function BrowserMiniPlayerShell({
  threadRef,
  miniPlayer,
  sourceSize,
  label,
  recording = false,
  onOpenInPanel,
  children,
}: BrowserMiniPlayerShellProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [layout, setLayout] = useState<BrowserMiniPlayerShellLayout | null>(null);
  const container = layout?.container ?? null;
  const obstacles = layout?.obstacles ?? NO_BROWSER_MINI_PLAYER_OBSTACLES;
  const sourceKey = browserMiniPlayerSourceKey(miniPlayer.source);
  const frame = container
    ? resolveBrowserMiniPlayerFrame({
        width: miniPlayer.width,
        position: miniPlayer.position,
        source: sourceSize,
        container,
        obstacles,
      })
    : null;

  const radius = BROWSER_MINI_PLAYER_CORNER_RADIUS;
  // Inside the curve the default 8px inset would land on the clipped corner.
  const pillInset = Math.max(8, Math.round(radius * 0.55));

  const { beginGesture, handlePointerMove, endGesture } = useBrowserMiniPlayerGestures({
    threadRef,
    sourceKey,
    frame,
    container,
    obstacles,
    sourceSize,
  });

  // The composer grows on its own (drafts, banners), so it is observed
  // alongside the column. The stack can mount late (an approval overlay may
  // hold its slot at mount) or be replaced by React, so a mutation observer
  // re-arms the resize observation on whatever stack is current; the
  // measurement itself re-queries the stack every time.
  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const measure = () => {
      const next = measureBrowserMiniPlayerLayout(element);
      setLayout((current) =>
        current && sameBrowserMiniPlayerLayout(current, next) ? current : next,
      );
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const root = element.closest<HTMLElement>('[data-slot="sidebar-inset"]');
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    if (root) observer.observe(root);
    let observedStack: Element | null = null;
    const observeCurrentStack = () => {
      const stack = root?.querySelector('[data-chat-composer-stack="true"]') ?? null;
      if (stack && stack !== observedStack) {
        observer.observe(stack);
        observedStack = stack;
      }
    };
    observeCurrentStack();
    const mutations = new MutationObserver(() => {
      observeCurrentStack();
      measure();
    });
    if (root) mutations.observe(root, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      mutations.disconnect();
    };
  }, []);

  return (
    <div ref={containerRef} className="pointer-events-none absolute inset-0">
      {frame ? (
        <section
          aria-label={label}
          data-browser-mini-player={sourceKey}
          className="pointer-events-none absolute select-none"
          style={{
            left: frame.x,
            top: frame.y,
            width: frame.width,
            height: frame.height,
            borderRadius: radius,
          }}
        >
          <BrowserMiniPlayerPill
            recording={recording}
            pillInset={pillInset}
            onOpenInPanel={onOpenInPanel}
            onDismiss={() => useBrowserMiniPlayerStore.getState().dismiss(threadRef)}
            beginGesture={beginGesture}
            handlePointerMove={handlePointerMove}
            endGesture={endGesture}
          />

          <div
            className="pointer-events-none absolute inset-0 rounded-[inherit] bg-muted shadow-2xl"
            style={{ zIndex: BROWSER_MINI_PLAYER_BACKDROP_Z_INDEX }}
          />
          <div
            className="absolute inset-0 overflow-hidden rounded-[inherit]"
            // Tags the live surface this frame mirrors, so client-side
            // consumers that must tell live surfaces apart (recording) can
            // bind to the requested tab.
            data-browser-tab-id={miniPlayer.source.tabId}
            style={{ zIndex: BROWSER_MINI_PLAYER_CONTENT_Z_INDEX }}
          >
            {children(frame)}
          </div>
          <div
            className="pointer-events-none absolute inset-0 rounded-[inherit] ring-1 ring-inset ring-border/80"
            style={{ zIndex: BROWSER_MINI_PLAYER_CHROME_Z_INDEX }}
          />
          {RESIZE_HANDLES.map(({ direction, className }) => (
            <div
              key={direction}
              role="presentation"
              data-browser-mini-player-resize={direction}
              className={cn(
                "pointer-events-auto absolute touch-none",
                HIT_AREA_PAD_CLASS,
                className,
              )}
              style={{ zIndex: BROWSER_MINI_PLAYER_CHROME_Z_INDEX }}
              onPointerDown={(event) => beginGesture(event, direction)}
              onPointerMove={handlePointerMove}
              onPointerUp={endGesture}
              onPointerCancel={endGesture}
            />
          ))}
        </section>
      ) : null}
    </div>
  );
}
